/**
 * Builds filter parameters for sleeving selector API requests
 * Handles extracting form values and constructing query strings
 */
export class FilterParamsBuilder {
  /**
   * @param {HTMLElement} formElement - The form element containing filter controls
   * @param {SliderManager} sliderManager - Slider manager instance
   */
  constructor(formElement, sliderManager) {
    this.formElement = formElement
    this.sliderManager = sliderManager
  }

  /**
   * Build URL query string from current form state
   * @returns {string} URL-encoded query string
   */
  build() {
    return new URLSearchParams({
      col: this.getOptionValue('pref-color', '0'),
      abraMin: this.getSliderValue('abrasion', 'min'),
      abraMax: this.getSliderValue('abrasion', 'max'),
      wrap: this.getOptionValue('pref-wrap', '2'),
      expMin: this.getSliderValue('expansion', 'min'),
      expMax: this.getSliderValue('expansion', 'max'),
      tool: this.getOptionValue('pref-tool', '2'),
      flex: this.getOptionValue('pref-flex', '0'),
      tempMin: this.getSliderValue('temperature', 'min'),
      tempMax: this.getSliderValue('temperature', 'max'),
      sort: this.getSortOrder()
    }).toString()
  }

  /**
   * Get value of selected option button
   * @param {string} name - Button name attribute
   * @param {string} defaultValue - Default value if none selected
   * @returns {string} Selected button value
   */
  getOptionValue(name, defaultValue) {
    return this.formElement.querySelector(`button[name='${name}'].active`)?.value || defaultValue
  }

  /**
   * Get slider value (min or max) for a given slider type
   * @param {string} sliderType - 'temperature', 'expansion', or 'abrasion'
   * @param {string} minOrMax - 'min' or 'max'
   * @returns {number} Slider value
   */
  getSliderValue(sliderType, minOrMax) {
    const isEnabled = this.isSliderEnabled(sliderType)
    const values = this.sliderManager.getFilterValues(sliderType, isEnabled)
    return values[minOrMax]
  }

  /**
   * Check if a slider is enabled via its toggle switch
   * @param {string} sliderType - 'temperature', 'expansion', or 'abrasion'
   * @returns {boolean} True if slider is enabled
   */
  isSliderEnabled(sliderType) {
    const toggleName = this.getToggleName(sliderType)
    return this.formElement.querySelector(`input[name='${toggleName}']`)?.checked !== false
  }

  /**
   * Get toggle switch name for a slider type
   * @param {string} sliderType - 'temperature', 'expansion', or 'abrasion'
   * @returns {string} Toggle input name
   */
  getToggleName(sliderType) {
    const toggleMap = {
      temperature: 'tempToggle',
      expansion: 'expToggle',
      abrasion: 'abraToggle'
    }
    return toggleMap[sliderType]
  }

  /**
   * Get current sort order value
   * @returns {string} Sort order value
   */
  getSortOrder() {
    return this.formElement.querySelector("select[name='sort-order']")?.value || 'catalog'
  }
}
